import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getStatusColor } from '../../utils';
import { formatFieldName, formatFieldValue, generateEditMessage, formatPaymentObject } from '../../utils/formatters';
import RelativeTime from '../RelativeTime';
import { PaperClipIcon } from '@heroicons/react/20/solid';
import FileAttachments from '../ScopeDetail/FileAttachments';

// Utility for classNames to avoid repetition
const classNames = (...classes) => classes.filter(Boolean).join(' ');

export default function RecentActivity({ limit = 100 }) {
  const { user } = useAuth(); // Keep this for future use if needed
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setLoading(true);
        const { fetchRecentActivity } = await import('../../utils/api');
        const data = await fetchRecentActivity(limit);
        setActivity(data);
      } catch (err) {
        console.error('Error fetching recent activity:', err);
        setError('Failed to load recent activity');
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [limit]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-20 bg-gray-100 dark:bg-gray-700 rounded-md"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{error}</h3>
          </div>
        </div>
      </div>
    );
  }

  if (activity.length === 0) {
    return (
      <div className="text-center py-12 bg-white dark:bg-gray-800 shadow sm:rounded-lg">
        <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No activity yet</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Activities will appear here when changes are made to the budget items.
        </p>
      </div>
    );
  }

  // Using shared functions from utils/formatters.js

  // Using the shared getStatusColor utility function from utils/helpers.js

  // No longer need comment submission handling for Overview page

  return (
    <div className="flow-root">
      <ul role="list" className="-mb-8">
        {activity.map((item, index) => (
          <li key={`${item.type}-${item.id || index}`}>
            <div className="relative pb-8">
              {index !== activity.length - 1 ? (
                <span aria-hidden="true" className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700" />
              ) : null}
              
              <div className="relative flex items-start space-x-3">
                {item.type === 'comment' ? (
                  <>
                    <div className="relative">
                      <div className="flex size-10 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 ring-8 ring-white dark:ring-gray-800">
                        <span className="text-indigo-700 dark:text-indigo-300 text-sm font-medium">
                          {item.user && item.user.slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <span className="absolute -right-1 -bottom-0.5 rounded-tl bg-white dark:bg-gray-800 px-0.5 py-px">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5 text-gray-400 dark:text-gray-500">
                          <path fillRule="evenodd" d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902 1.168.188 2.352.327 3.55.414.28.02.521.18.642.413l1.713 3.293a.75.75 0 0 0 1.33 0l1.713-3.293a.783.783 0 0 1 .642-.413 41.102 41.102 0 0 0 3.55-.414c1.437-.231 2.43-1.49 2.43-2.902V5.426c0-1.413-.993-2.67-2.43-2.902A41.289 41.289 0 0 0 10 2Z" clipRule="evenodd" />
                        </svg>
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div>
                        <div className="text-sm">
                          <Link to={`/construction/budget/${encodeURIComponent(item.short_name)}`} className="font-medium text-indigo-600 hover:underline">
                            {item.scope}
                          </Link>{' '}
                          <span className="font-medium text-gray-900">
                            by {item.user}
                          </span>
                        </div>
                        <p className="mt-0.5 text-sm text-gray-500">
                          Commented <RelativeTime timestamp={item.timestamp} />
                          {item.edited && (
                            <span className="ml-1 text-xs text-gray-400">
                              <RelativeTime timestamp={item.edited.timestamp} showEditedPrefix={true} />
                            </span>
                          )}
                          {/* Show file attachment indicator if comment has files */}
                          {item.files && item.files.length > 0 && (
                            <span className="ml-2 inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                              <PaperClipIcon className="h-3.5 w-3.5 mr-0.5" />
                              {item.files.length}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="mt-2 text-sm text-gray-700">
                        <p>{item.content}</p>
                      </div>
                      {/* Display file attachments if any */}
                      {item.files && item.files.length > 0 && (
                        <FileAttachments files={item.files} isReadOnly={true} />
                      )}
                    </div>
                  </>
                ) : item.type === 'edit' ? (
                  <>
                    <div>
                      <div className="relative px-1">
                        <div className="flex size-8 items-center justify-center rounded-full bg-gray-100 ring-8 ring-white">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5 text-gray-500">
                            <path d="m5.433 13.917 1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z" />
                            <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 py-1.5">
                      <div className="text-sm text-gray-500">
                        <Link to={`/construction/budget/${encodeURIComponent(item.short_name)}`} className="font-medium text-indigo-600 hover:underline">
                          {item.scope}
                        </Link>{' '}
                        <span className="font-medium text-gray-900">
                          by {item.user}
                        </span>{' '}
                        
                        {/* Render edit message based on field type */}
                        {item.field === 'status' ? (
                          <>
                            changed status from{' '}
                            <span className={classNames("inline-block px-2 py-0.5 text-xs font-semibold rounded-full", getStatusColor(item.old_value))}>
                              {item.old_value}
                            </span>{' '}
                            to{' '}
                            <span className={classNames("inline-block px-2 py-0.5 text-xs font-semibold rounded-full", getStatusColor(item.new_value))}>
                              {item.new_value}
                            </span>
                          </>
                        ) : item.field === 'percentage_complete' ? (
                          <>
                            updated {formatFieldName(item.field)} from{' '}
                            <span className="font-medium text-blue-600">
                              {formatFieldValue(item.field, item.old_value)}
                            </span>{' '}
                            to{' '}
                            <span className="font-medium text-blue-600">
                              {formatFieldValue(item.field, item.new_value)}
                            </span>
                          </>
                        ) : item.field === 'notes' ? (
                          <>updated notes</>
                        ) : (item.field === 'payment_added') ? (
                          <>
                            added payment{' '}
                            <div className="mt-2 mb-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-600 dark:text-gray-300 text-xs">
                              <div>
                                <span className="font-semibold">Details:</span>
                                <div className="pl-2 border-l-2 border-indigo-300 mt-1 whitespace-pre-wrap">
                                  {formatPaymentObject(item.new_value, item).fullText}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (item.field === 'payment_deleted') ? (
                          <>
                            removed payment{' '}
                            <div className="mt-2 mb-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-600 dark:text-gray-300 text-xs">
                              <div>
                                <span className="font-semibold">Details:</span>
                                <div className="pl-2 border-l-2 border-gray-300 mt-1 whitespace-pre-wrap">
                                  {formatPaymentObject(item.old_value, item).fullText}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (item.field === 'payment_updated') ? (
                          <>
                            updated payment{' '}
                            <div className="mt-2 mb-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-600 dark:text-gray-300 text-xs">
                              <div className="mb-2">
                                <span className="font-semibold">Previous:</span>
                                <div className="pl-2 border-l-2 border-gray-300 mt-1 whitespace-pre-wrap">
                                  {formatPaymentObject(item.old_value, item).fullText}
                                </div>
                              </div>
                              <div>
                                <span className="font-semibold">Updated:</span>
                                <div className="pl-2 border-l-2 border-indigo-300 mt-1 whitespace-pre-wrap">
                                  {formatPaymentObject(item.new_value, item).fullText}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (['contract_added', 'contract_deleted', 'contract_updated'].includes(item.field)) ? (
                          <>
                            {generateEditMessage(item.field, item.old_value, item.new_value)}
                          </>
                        ) : (item.old_value === null || item.old_value === undefined || item.old_value === '') && 
                           (item.new_value !== null && item.new_value !== undefined && item.new_value !== '') ? (
                          <>
                            added {formatFieldName(item.field)}{' '}
                            <span className="font-medium">
                              {formatFieldValue(item.field, item.new_value)}
                            </span>
                          </>
                        ) : (item.new_value === null || item.new_value === undefined || item.new_value === '') && 
                          (item.old_value !== null && item.old_value !== undefined && item.old_value !== '') ? (
                          <>
                            removed {formatFieldName(item.field)}{' '}
                            <span className="font-medium">
                              {formatFieldValue(item.field, item.old_value)}
                            </span>
                          </>
                        ) : (
                          <>
                            updated {formatFieldName(item.field)} from{' '}
                            <span className="font-medium">
                              {formatFieldValue(item.field, item.old_value)}
                            </span>{' '}
                            to{' '}
                            <span className="font-medium">
                              {formatFieldValue(item.field, item.new_value)}
                            </span>
                          </>
                        )}
                        
                        {' '}
                        <RelativeTime timestamp={item.timestamp} />
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}